import { logCommonView } from '@/lib/service';
import { EventSource } from '@/shared/constants/event';
import { LOCATION_OPTIONS } from '@/shared/constants/sourcing-marketplace';
import { useQueryParams } from '@/shared/hooks';
import { CatalogCategory, SearchCountryCode } from '@/shared/types/catalog';
import { CategoryConfig, VerticalName } from '@/shared/types/category';
import {
  AverageReviewFilterOptions,
  ISourcingFilterOption,
  MoqFilterOptions,
  OrderVolumeFilterOptions,
  ProductionTimeFilterOptions,
  SamplePriceFilterOptions,
  ViewType,
} from '@/shared/types/sourcing-marketplace';
import { ParamKey } from '@/shared/types/url-params';
import React from 'react';
import './index.less';
import FilterContainer from './FilterContainer';
import Rating from '@/components/Common/Rating';

interface IProps {
  viewType: ViewType;
}

const SourcingFilters = ({ viewType }: IProps) => {
  const { queryParams, setQueryParams } = useQueryParams<{}>();

  // filter
  const region = queryParams.get(ParamKey.REGION);
  const countryCode = queryParams.get(ParamKey.COUNTRY_CODE);
  const selectedSamplePriceFilter = queryParams.get(ParamKey.SAMPLE_PRICE);
  const selectedMOQFilter = queryParams.get(ParamKey.MOQ);
  const selectedAverageReviewFilter = queryParams.get(ParamKey.AVERAGE_REVIEW);
  const selectedProductionTimeFilter = queryParams.get(ParamKey.PRODUCTION_TIME);

  const [locationState, setLocationState] = React.useState<string | null>(region || countryCode);
  const [samplePriceState, setSamplePriceState] = React.useState<string | null>(selectedSamplePriceFilter);
  const [moqState, setMoqState] = React.useState<string | null>(selectedMOQFilter);
  const [averageReviewState, setAverageReviewState] = React.useState<string | null>(selectedAverageReviewFilter);
  const [productionTimeState, setProductionTimeState] = React.useState<string | null>(selectedProductionTimeFilter);

  const setRegionOrCountryCode = (value: string) => {
    setLocationState(value);

    if (value === SearchCountryCode.CN || value === SearchCountryCode.US) {
      setQueryParams({ [ParamKey.COUNTRY_CODE]: value, [ParamKey.REGION]: null });
    } else {
      setQueryParams({ [ParamKey.REGION]: value, [ParamKey.COUNTRY_CODE]: null });
    }
  };

  const setSelectedSamplePriceFilter = (newValue: string) => {
    setSamplePriceState(newValue);
    setQueryParams({ [ParamKey.SAMPLE_PRICE]: newValue });
  };

  const setSelectedMoqFilter = (newValue: string) => {
    setMoqState(newValue);
    setQueryParams({ [ParamKey.MOQ]: newValue });
  };

  const setSelectedAverageReviewFilter = (newValue: string) => {
    setAverageReviewState(newValue);
    setQueryParams({ [ParamKey.AVERAGE_REVIEW]: newValue });
  };

  const setSelectedProductionTimeFilter = (newValue: string) => {
    setProductionTimeState(newValue);
    setQueryParams({ [ParamKey.PRODUCTION_TIME]: newValue });
  };

  const renderLocationFilter = () => {
    return (
      <FilterContainer
        title="Location"
        defaultActiveKey={['Location']}
        options={LOCATION_OPTIONS}
        selectedOption={locationState}
        onOptionClick={(option: ISourcingFilterOption) => {
          logCommonView(EventSource.LOCATION_FILTER_UPDATED, `${viewType}_${option?.label}`);
          setRegionOrCountryCode(option?.value);
        }}
      />
    );
  };

  const renderSamplePriceFilter = () => {
    return (
      <FilterContainer
        title="Sampling price range"
        defaultActiveKey={['Sampling price range']}
        options={SamplePriceFilterOptions}
        selectedOption={samplePriceState}
        onOptionClick={(option: ISourcingFilterOption) => {
          logCommonView(EventSource.SAMPLE_PRICE_FILTER_UPDATED, `${viewType}_${option?.label}`);
          setSelectedSamplePriceFilter(option?.value);
        }}
      />
    );
  };

  const renderMoqFilter = () => {
    return (
      <FilterContainer
        title="Production minimum"
        defaultActiveKey={['Production minimum']}
        options={MoqFilterOptions}
        selectedOption={moqState}
        onOptionClick={(option: ISourcingFilterOption) => {
          logCommonView(EventSource.MOQ_FILTER_UPDATED, `${viewType}_${option?.label}`);
          setSelectedMoqFilter(option?.value);
        }}
      />
    );
  };

  const renderAverageReviewFilter = () => {
    return (
      <FilterContainer
        title="Average review"
        defaultActiveKey={['Average review']}
        options={AverageReviewFilterOptions}
        selectedOption={averageReviewState}
        onOptionClick={(option: ISourcingFilterOption) => {
          logCommonView(EventSource.AVERAGE_REVIEW_FILTER_UPDATED, `${viewType}_${option?.label}`);
          setSelectedAverageReviewFilter(option?.value);
        }}
        renderItemLabel={(item) =>
          item?.value ? <Rating rate={Number(item?.value)} label={item?.label} /> : item?.label
        }
      />
    );
  };

  const renderProductionTime = () => {
    return (
      <FilterContainer
        title="Production time"
        options={ProductionTimeFilterOptions}
        selectedOption={productionTimeState}
        onOptionClick={(option: ISourcingFilterOption) => {
          logCommonView(EventSource.PRODUCTION_TIME_FILTER_UPDATED, `${viewType}_${option?.label}`);
          setSelectedProductionTimeFilter(option?.value);
        }}
      />
    );
  };

  return (
    <div className="sourcing-filters">
      {renderLocationFilter()}
      {renderSamplePriceFilter()}
      {renderMoqFilter()}
      {renderAverageReviewFilter()}
      {renderProductionTime()}
    </div>
  );
};

export default SourcingFilters;
